/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React from 'react';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buttonStyles } from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import { JobNameContainer } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import CustomDropDownAutoComplete from '../../../../../Shared/Components/Common/CustomDropDown/CustomDropdownAutoComplete';
import Modal from '../../../../Jobs/Utilities/Shared/Components/Modal';
import { IAddSubTaskModalProps } from './ModifyServices.Model';
import { styles } from './ModifyServices.Style';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { NoteText } from '../AllJobs.Style';

const AddSubTaskModal: React.FC<IAddSubTaskModalProps> = ({
  open,
  onClose,
  createNewSubTask,
  setCreateNewSubTask,
  subTask,
  setSubTask,
  subTaskForm,
  updateSubTaskForm,
  handleSubTaskTypeChange,
  taskOptions,
  handleSubTaskChanges,
  isTasksLoading,
  totalCount,
  currentPage,
  handlePaginationChange,
  searchString,
  setSearchString,
  handleConfirmButton,
  isTaskCreationLoading,
  isTaskSubTaskMapLoading,
  isServiceTaskLoading,
  isConfirmButtonLoading,
}) => {
  const { textField, radioButton, radioButtonText } = buttonStyles;
  const { t } = useTranslation(['taskManagement']);

  const renderRadioOptions = () => (
    <FormControl component="fieldset">
      <Typography sx={styles.sectionTitle}>
        <span>
          {t('taskManagement:DoYouWantToAddSubTaskPermanentlyOrSchedule')}
          <span className="mandatory-field"> *</span>
        </span>
      </Typography>
      <RadioGroup value={subTaskForm.type} onChange={handleSubTaskTypeChange}>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value="PERMANENT"
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:Permanently')}
            sx={radioButtonText}
          />
        </Box>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value="SCHEDULE"
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:Schedule')}
            sx={radioButtonText}
          />
        </Box>
      </RadioGroup>
    </FormControl>
  );

  const renderNote = () => (
    <Box sx={{ marginTop: '2rem' }}>
      <NoteText>{t('taskManagement:PermanentTaskImpactNote')}</NoteText>
    </Box>
  );

  const renderCreateSubTaskForm = () => (
    <Box sx={{ marginTop: '2rem' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="subtask-name"
            label={
              <span>
                {t('taskManagement:SubTaskName')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={subTaskForm.name}
            onChange={(e) => updateSubTaskForm('name', e.target.value)}
          />
        </JobNameContainer>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="subtask-description"
            label={
              <span>
                {t('taskManagement:SubTaskDescription')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={subTaskForm.description}
            onChange={(e) => updateSubTaskForm('description', e.target.value)}
          />
        </JobNameContainer>
      </Box>
      {renderRadioOptions()}
      {renderNote()}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
        <Button
          type={ButtonType.Secondary}
          label={t('taskManagement:Cancel')}
          onClick={() => setCreateNewSubTask(false)}
        />
        <Button
          type={ButtonType.Primary}
          label={t('taskManagement:CreateSubTaskButton')}
          onClick={handleConfirmButton}
          isLoading={isTaskCreationLoading || isTaskSubTaskMapLoading || isServiceTaskLoading}
          disabled={
            !subTaskForm.name ||
            !subTaskForm.description ||
            !subTaskForm.type ||
            isTaskCreationLoading ||
            isTaskSubTaskMapLoading
          }
        />
      </Box>
    </Box>
  );

  const renderExistingSubTaskForm = () => (
    <>
      <Box sx={{ marginTop: '2rem' }}>{renderRadioOptions()}</Box>
      {renderNote()}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
        <Button
          type={ButtonType.Secondary}
          label={t('taskManagement:Cancel')}
          onClick={() => {
            setSubTask('');
            onClose();
          }}
        />
        <Button
          type={ButtonType.Primary}
          label={t('taskManagement:Confirm')}
          onClick={handleConfirmButton}
          disabled={
            !subTask || !subTaskForm.type || isTaskSubTaskMapLoading || isConfirmButtonLoading || isServiceTaskLoading
          }
          isLoading={isConfirmButtonLoading || isTaskSubTaskMapLoading}
        />
      </Box>
    </>
  );

  const renderBodyContent = () => {
    return (
      <Box>
        <Typography sx={styles.sectionTitle}>{t('taskManagement:SubTask')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <CustomDropDownAutoComplete
            title={t('taskManagement:SubTasks')}
            options={taskOptions}
            selectedValue={!createNewSubTask ? subTask : ''}
            onChange={handleSubTaskChanges}
            isLoading={isTasksLoading}
            totalCounts={totalCount}
            handlePaginationChanges={handlePaginationChange}
            currentPages={currentPage}
            searchString={searchString}
            setSearchString={setSearchString}
            dropDownHeight={500}
            isInline={true}
            isDisabled={createNewSubTask}
          />
          <Box
            sx={{
              ...styles.createNewLink,
              opacity: createNewSubTask ? '0.5' : '1',
              cursor: createNewSubTask ? 'not-allowed' : 'pointer',
            }}
            onClick={() => !createNewSubTask && setCreateNewSubTask(true)}>
            <img src={Icons.AddIcon} width={24} height={24} alt="add" />
            {t('taskManagement:CreateSubTask')}
          </Box>
        </Box>
        {createNewSubTask ? renderCreateSubTaskForm() : renderExistingSubTaskForm()}
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      renderHeader={''}
      renderBody={renderBodyContent()}
      styles={{
        '& .MuiPaper-root': {
          width: '52rem',
        },
      }}
      isRemoveDivider={true}
    />
  );
};

export default AddSubTaskModal;
